import { faEnvelope, faLock, faPaperPlane, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useState } from "react";
import { useApiCall } from "../api/api";
import type { Exam } from "../api/dto";
import { Button } from "../components/form/Button";
import { Input } from "../components/form/Input";
import { Toggle } from "../components/form/Toggle";
import { ModalFooter } from "../components/layout/ModalProvider";

/** fixedRecipient always receives the report, it is shown first and can't be edited or removed. */
const fixedRecipient = "testing@dijkgraaf-support.com";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** SendExamReportModal sends the report of an exam by email, with the same options as the tablet app. */
export const SendExamReportModal: FC<{ exam: Exam }> = (props) => {
  const { sendExamReport } = useApiCall();
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [includePdf, setIncludePdf] = useState(true);
  const [separateContractors, setSeparateContractors] = useState(false);
  const [includeCsv, setIncludeCsv] = useState(true);
  const [includeProjectCsv, setIncludeProjectCsv] = useState(false);
  const [includeCertificate, setIncludeCertificate] = useState(false);
  const [includeFullExport, setIncludeFullExport] = useState(false);

  const trimmedEmail = newEmail.trim();
  const isDuplicate = (email: string) =>
    [fixedRecipient, ...emails].some((existing) => existing.toLowerCase() === email.toLowerCase());
  const canAddEmail = isValidEmail(trimmedEmail) && !isDuplicate(trimmedEmail);
  const emailError =
    trimmedEmail === ""
      ? null
      : isValidEmail(trimmedEmail)
        ? isDuplicate(trimmedEmail)
          ? "Dit e-mailadres staat al in de lijst."
          : null
        : trimmedEmail.includes("@")
          ? "Dit is geen geldig e-mailadres."
          : null;

  /** addEmails adds every valid, new address in the text (separated by spaces, commas or semicolons). */
  const addEmails = (text: string) => {
    const candidates = text.split(/[\s,;]+/).filter(Boolean);
    const added: string[] = [];
    const rejected: string[] = [];
    for (const candidate of candidates) {
      const known = [fixedRecipient, ...emails, ...added];
      if (
        isValidEmail(candidate) &&
        !known.some((email) => email.toLowerCase() === candidate.toLowerCase())
      ) {
        added.push(candidate);
      } else if (!isValidEmail(candidate)) {
        rejected.push(candidate);
      }
    }
    setEmails((old) => [...old, ...added]);
    // Keep what isn't a valid address in the field, so the user can correct it.
    setNewEmail(rejected.join(", "));
  };

  const onEmailKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addEmails(newEmail);
    }
  };

  const onEmailPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const text = event.clipboardData.getData("text");
    if (/[\s,;]/.test(text.trim())) {
      // Several addresses were pasted at once.
      event.preventDefault();
      addEmails(`${newEmail} ${text}`);
    }
  };

  // The report needs a PDF, either the overall one or the one per contractor.
  const changeIncludePdf = (value: boolean) => {
    setIncludePdf(value);
    if (!value && !separateContractors) setSeparateContractors(true);
  };
  const changeSeparateContractors = (value: boolean) => {
    setSeparateContractors(value);
    if (!value && !includePdf) setIncludePdf(true);
  };

  const send = async () => {
    const recipients = [fixedRecipient, ...emails, ...(canAddEmail ? [trimmedEmail] : [])];
    await sendExamReport(props.exam.id, {
      emails: recipients,
      includePdf,
      separateContractors,
      includeCsv,
      includeProjectCsv,
      includeCertificate,
      includeFullExport,
    });
  };

  return (
    <div className={"space-y-4"}>
      <p>Verstuur het rapport naar een of meerdere e-mailadressen.</p>
      <div className={"font-semibold"}>&middot; {props.exam.name}</div>

      <div>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: only listens to the key and paste events of the input inside */}
        <div onKeyDown={onEmailKeyDown} onPaste={onEmailPaste}>
          <div className={"flex items-end gap-2"}>
            <Input
              type={"text"}
              label={"E-mailadres"}
              placeholder={"naam@bedrijf.nl"}
              value={newEmail}
              onChange={setNewEmail}
              maxWidth={"full"}
              focus
            />
            <Button
              type={"secondary"}
              size={"md"}
              text={"Toevoegen"}
              disabled={!canAddEmail}
              onClick={() => addEmails(newEmail)}
            />
          </div>
          {emailError ? (
            <p className={"mt-1 text-sm text-red-600"}>{emailError}</p>
          ) : (
            <p className={"mt-1 text-xs text-slate-500"}>
              Druk op Enter om toe te voegen.
            </p>
          )}
        </div>

        <div className={"mt-4 flex items-center justify-between"}>
          <div className={"font-semibold"}>
            Ontvangers
            <span
              className={
                "ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
              }
            >
              {emails.length + 1}
            </span>
          </div>
          {emails.length > 1 && (
            <button
              type={"button"}
              className={"text-sm text-slate-500 underline hover:text-red-600"}
              onClick={() => setEmails([])}
            >
              Alles verwijderen
            </button>
          )}
        </div>
        <ul className={"mt-2 divide-y divide-slate-100 rounded-sm border border-slate-200"}>
          <li className={"flex items-center gap-3 bg-slate-50 px-3 py-2"}>
            <span
              className={
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-800"
              }
            >
              <FontAwesomeIcon icon={faEnvelope} className={"text-sm"} />
            </span>
            <span className={"min-w-0 flex-1 truncate"}>{fixedRecipient}</span>
            <span
              title={"Dit e-mailadres ontvangt altijd het rapport"}
              className={"flex items-center gap-1 text-xs text-slate-500"}
            >
              <FontAwesomeIcon icon={faLock} />
              Vast
            </span>
          </li>
          {emails.map((email) => (
            <li key={email} className={"flex items-center gap-3 px-3 py-2 hover:bg-slate-50"}>
              <span
                className={
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-800"
                }
              >
                <FontAwesomeIcon icon={faEnvelope} className={"text-sm"} />
              </span>
              <span className={"min-w-0 flex-1 truncate"}>{email}</span>
              <button
                type={"button"}
                aria-label={`Verwijder ${email}`}
                title={"Verwijderen"}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-sm text-slate-400 hover:bg-red-50 hover:text-red-600"
                }
                onClick={() => setEmails((old) => old.filter((e) => e !== email))}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className={"mb-2 font-semibold"}>Verzendopties</div>
        <div className={"space-y-2"}>
          <Toggle
            label={"Inclusief: Algemeen Rapport (PDF)"}
            value={includePdf}
            onChange={changeIncludePdf}
          />
          <Toggle
            label={"Inclusief: Rapport per Contractor (PDF)"}
            value={separateContractors}
            onChange={changeSeparateContractors}
          />
          <Toggle
            label={"Inclusief: Sessie Rapport (Excel)"}
            value={includeCsv}
            onChange={setIncludeCsv}
          />
          <Toggle
            label={"Inclusief: Project Rapport (Excel)"}
            value={includeProjectCsv}
            onChange={setIncludeProjectCsv}
          />
          <Toggle
            label={"Inclusief: Certificaat per participant (PDF)"}
            value={includeCertificate}
            onChange={setIncludeCertificate}
          />
          <Toggle
            label={"Inclusief: Volledige export (CSV)"}
            value={includeFullExport}
            onChange={setIncludeFullExport}
          />
        </div>
      </div>

      <ModalFooter text={"Versturen"} icon={faPaperPlane} onSubmit={send} />
    </div>
  );
};
